import { ObjectChecker } from '@payments-checkout/shared/helpers/index';
import { useParams } from 'next/navigation';
import { useIntl } from 'react-intl';
import { usePaymentLinkId } from '../../../hooks/usePaymentLinkId';
import { recurrenceDetailItemsBuilder } from '../../helpers/formatRecurrenceDetail';
import { getFrequencyLabel } from '../../helpers/frequencyMapper';

const buildKey = (repetition: number) => {
  if(repetition === 1) {
    return 'recurrence.interval.time';
  }
  return 'recurrence.interval.times';
}

export const useRecurrenceDetails = () => {
  const params = useParams<{ paymentLinkId: string }>();
  const paymentLinkIdData = usePaymentLinkId(params.paymentLinkId);
  const { formatMessage, locale } = useIntl();

  const isReady =
    paymentLinkIdData.isFetched &&
    ObjectChecker.isFilled(paymentLinkIdData.data);

  if (isReady) {
    const filteredDetails = recurrenceDetailItemsBuilder({
      locale,
      pureItems: paymentLinkIdData.data?.cart_settings?.recurrences,
    });

    const interval = filteredDetails[0].plan.interval;
    const intervalCount = parseInt(filteredDetails[0].plan.intervalCount, 10);
    const frequencyLabel = getFrequencyLabel(interval, intervalCount);

    const basicInfo = [
      {
        key: formatMessage({ id: 'recurrence.interval.frequency' }),
        value: formatMessage(
          { id: frequencyLabel.i18nKey },
          { count: frequencyLabel.count }
        ) || frequencyLabel.fallback,
      },
      {
        key: formatMessage({ id: 'recurrence.interval.charge' }),
        value:
          filteredDetails[0].plan.cycles > 0
            ? `${filteredDetails[0].plan.cycles} ${formatMessage({
                id: buildKey(filteredDetails[0].plan.cycles),
              })}`
            : formatMessage({ id: 'recurrence.interval.withoutLimitCycles' }),
      },
      {
        key:  formatMessage({ id: 'recurrence.interval.recurrenceValue' }),
        value: filteredDetails[0].plan.totalCost,
      },
    ];

    if(filteredDetails[0].plan.membershipTax) {
      basicInfo.push({
        key: formatMessage({ id: 'recurrence.membershipTax' }),
        value: filteredDetails[0].plan.membershipTax,
      });
    }

    if (filteredDetails[0].plan.startAt) {
      basicInfo.unshift({
        key: formatMessage({ id: 'recurrence.interval.startAt' }),
        value: filteredDetails[0].plan.startAt,
      })
    }
    return {
      detailRecurrenceItems: filteredDetails,
      formattedInfo: basicInfo,
    };
  }

  return { detailRecurrenceItems: [], formattedInfo: [] };
};
